import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '../../styles/stitches.config';
import { useUser } from '../../contextManager/user.context';
import { useEvents } from '../../hooks/useEvents';
import { useToast } from '../../contextManager/toast.context';
import { AxiosContext } from '../../contextManager/axios.context';
import { useContext } from 'react';
import { PencilSquare } from 'react-bootstrap-icons';
import type { CalendarEvent } from '../../hooks/useEvents';
import { CameraVideo } from 'react-bootstrap-icons';
import { getFirstName, getInitial } from '../../utils/name';
import {
  TutorDetailCard3 as ShellCard,
  CardFooter2,
  PrimaryButton,
  SecondaryButton,
  TagWrap,
  CornerTag,
  TutorDetailWrapper2,
  ProfileInitial,
  NameAndPriceWrapper,
  TutorName as Title,
  TutorInfo,
  TutorImage,
} from '../../styles/search/search.style';
import { XCircleFill, Paperclip, X } from 'react-bootstrap-icons';
import {
  SubheaderContainer,
  SubheaderLine,
  SubheaderTitle,
  DescriptionText,
  DetailPageShell, 
  CardContent,
  CardHeader,
  BannerIconButton, 
  HeaderInfo,
  TruncatedTitle
} from '../../styles/profile/form.style';
import { endpoints } from '../../constants/endpoints';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { formatRangeInTz, friendlyTzName, shortTzName, getDetectedTimezone, wallClockToISO, wallClockEndToISO } from '../../utils/timezones';
import { useConnections } from '../../hooks/useConnections';
import { getTimezoneOffset, adjustAvailabilityForTimezone, DAYS_SUNDAY_FIRST, GRANULARITY } from '../../utils/availability';
import { AvailabilityObject } from '../../types/schemas/abc';
import { Globe } from 'lucide-react';
import { ManualField, ManualInput, ManualLabel } from '../profile/ProfileDetails';
import { OverlayTextarea } from '../../styles/login.style';
import { DropzoneContainer } from '../../styles/upload-dialog.style';
import Dropzone from 'react-dropzone';

/* ---------- Local styles (kept minimal and consistent) ---------- */
const Row = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
});

const Subtle = styled('div', {
  color: '$gray10',
  fontSize: '$sm',
});

const When = styled('div', {
  fontWeight: 600,
  color: '$gray12',
  fontSize: '$md',
});

const Section = styled('div', {
  display: 'grid',
  gap: '0.75rem',
});

// NEW: A dedicated container for edit-mode form fields for better structure
const EditFormSection = styled('div', {
  display: 'grid',
  gap: '1rem',
  width: '100%',
  paddingBlock: '0.5rem',
});


const Label = styled('label', {
  fontSize: '$sm',
  color: '$gray11',
  fontWeight: 500, // Added for clarity
  marginBottom: '-0.25rem', // Tighten space between label and input
});

const TextArea = styled('textarea', {
  width: '100%',
  minHeight: '120px',
  padding: '0.75rem',
  border: '1px solid $gray6',
  borderRadius: '8px',
  fontSize: '$sm',
  resize: 'vertical',
  outline: 'none',
  '&:focus': { borderColor: '$gray9' },
});

const FileRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
});

const LinkButton = styled('a', {
  textDecoration: 'none',
  fontSize: '$sm',
  color: '$indigo11',
  '&:hover': { textDecoration: 'underline' },
});

const SmallButton = styled('button', {
  padding: '0.4rem 0.65rem',
  fontSize: '$sm',
  borderRadius: '8px',
  border: '1px solid $sand11',
  background: 'white',
  cursor: 'pointer',
  '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
});

const MeetContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.5rem',
  color: '$blue11',

  '&svg': {
    stroke: '$blue11',
  },

  
  // marginTop: '1rem',
  flexWrap: 'wrap',
});

const MeetLinkBox = styled('a', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.6rem 0.9rem',
  borderRadius: '10px',
  border: '1px solid $blue7',
  backgroundColor: '$blue3',
  fontSize: '$sm',
  color: '$blue11',
  fontWeight: 600,
  textDecoration: 'none',
  '&:hover': { backgroundColor: '$blue4' },
  '&:active': { transform: 'translateY(1px)' },
  '&svg': {
    stroke: '$blue11',
  },



  
});

const Input = styled('input', {
  width: '100%',
  padding: '0.5rem 0.2rem',
  border: '1px solid $gray6',
  borderRadius: '8px',
  fontSize: '$sm',
  outline: 'none',
  '&:focus': { borderColor: '$gray9' },
});


const TimeInput = styled(Input, {
  border: 'black', // The nice blue border
  borderWidth: '1px',
  backgroundColor: '$blue2',
});


export type AvatarInfo = {
    name?: string;
    photoUrl?: string | null;
    color?: string | null;
  };
  
  const getFileName = (url: string): string => {
    if (!url) return '';
    try {
      const decoded = decodeURIComponent(url);
      const parts = decoded.split('/');
      const nameWithParams = parts[parts.length - 1];
      return nameWithParams.split('?')[0] || '';
    } catch {
      return 'File'; // Fallback
    }
  };
  

function fmtRange(startISO: string, endISO: string, tz: string) {
  const s = new Date(startISO);
  const e = new Date(endISO);
  const date = formatInTimeZone(s, tz, 'EEE d MMM');
  const range = formatRangeInTz(s, e, tz);
  return { date, range };
}

function initialsFrom(name?: string) {
    return getInitial(name);
  }

function getCounterpartyName(ev: CalendarEvent, currentUserId?: string) {
  if (!currentUserId) return getFirstName(ev.parentName || ev.tutorName);
  if (ev.tutorId === currentUserId) return getFirstName(ev.parentName);
  if (ev.parentId === currentUserId) return getFirstName(ev.tutorName);
  return getFirstName(ev.parentName || ev.tutorName);
}


function toTzInputValue(iso: string, tz: string) {
  return formatInTimeZone(new Date(iso), tz, "yyyy-MM-dd'T'HH:mm");
}
function fromTzInputValue(localValue: string, tz: string) {
  const naive = new Date(localValue);
  return fromZonedTime(naive, tz).toISOString();
}


interface LessonDetailViewProps {
  event: CalendarEvent;
  onClose: () => void;
  isTutor?: boolean;
  currentUserId?: string;
  avatar?: AvatarInfo;
  initialTz?: string;
}


export const LessonDetailView: React.FC<LessonDetailViewProps> = ({
  event,
  onClose,
  isTutor,
  avatar,
  currentUserId,
  initialTz,
}) => {
  const { userDetails } = useUser();
  const { connections } = useConnections();
  const homeTz = userDetails?.timezone || 'Europe/London';
  const browserTz = useMemo(() => getDetectedTimezone(), []);

  // Find counterparty timezone from connections
  const counterpartyId = currentUserId === event.tutorId ? event.parentId : event.tutorId;
  const counterpartyTz = useMemo(() => {
    for (const conn of connections) {
      if (conn.tutor?.id === counterpartyId) return conn.tutor.timezone;
      if (conn.parent?.id === counterpartyId) return conn.parent.timezone;
    }
    return undefined;
  }, [connections, counterpartyId]);

  // Build all unique timezones with labels
  const cpLabel = isTutor ? "Parent's" : "Tutor's";
  const allTzEntries = useMemo(() => {
    // Build labels per unique timezone, merging when multiple roles share the same tz
    const tzMap = new Map<string, { key: string; parts: string[] }>();

    // Home is always present
    const homeEntry = tzMap.get(homeTz) || { key: 'home', parts: [] };
    homeEntry.parts.push('Home');
    tzMap.set(homeTz, homeEntry);

    // Browser tz
    const browserEntry = tzMap.get(browserTz) || { key: 'browser', parts: [] };
    browserEntry.parts.push('Current');
    tzMap.set(browserTz, browserEntry);

    // Counterparty tz
    if (counterpartyTz) {
      const cpEntry = tzMap.get(counterpartyTz) || { key: 'counterparty', parts: [] };
      cpEntry.parts.push(cpLabel);
      tzMap.set(counterpartyTz, cpEntry);
    }

    return Array.from(tzMap.entries()).map(([tz, { key, parts }]) => ({
      key,
      tz,
      label: `${parts.join(' / ')} timezone (${friendlyTzName(tz)})`,
    }));
  }, [homeTz, browserTz, counterpartyTz, cpLabel]);

  const [activeTzKey, setActiveTzKey] = useState(() => {
    if (!initialTz) return 'home';
    if (initialTz === homeTz) return 'home';
    if (initialTz === browserTz) return 'browser';
    return 'home';
  });
  const activeTz = allTzEntries.find(o => o.key === activeTzKey)?.tz || homeTz;

  // Buttons to show = all entries except the currently active one
  const tzSwitchButtons = allTzEntries.filter(e => e.key !== activeTzKey);

  const { date, range } = fmtRange(event.startAt, event.endAt, activeTz);
  const who = useMemo(() => getCounterpartyName(event, currentUserId), [event, currentUserId]);

  const baseTitle = event.title || (event.eventType === 'lesson' ? 'Lesson' : 'Intro Call');
  const fullTitle = who ? `${baseTitle} with ${who}` : baseTitle;

  const { addToast } = useToast();
  const navigate = useNavigate();
  const { updateEvent, deleteEvent, events: myEvents } = useEvents();
  const axios = useContext(AxiosContext);

  // Tutor's timezone and availability for the time editor
  const tutorTz = useMemo(() => {
    if (isTutor) return homeTz; // I am the tutor
    for (const conn of connections) {
      if (conn.tutor?.id === event.tutorId) return conn.tutor.timezone;
    }
    return undefined;
  }, [connections, event.tutorId, isTutor, homeTz]);

  const tutorAvailability = useMemo((): AvailabilityObject | null => {
    if (isTutor) return userDetails?.availability ?? null;
    for (const conn of connections) {
      if (conn.tutor?.id === event.tutorId) return conn.tutor.availability ?? null;
    }
    return null;
  }, [connections, event.tutorId, isTutor, userDetails]);

  // Fetch tutor's busy slots
  const [tutorBusySlots, setTutorBusySlots] = useState<{ startAt: string; endAt: string }[]>([]);
  useEffect(() => {
    if (!event.tutorId) return;
    let cancelled = false;
    axios.get(endpoints.busyTimes(event.tutorId))
      .then(res => { if (!cancelled) setTutorBusySlots(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [event.tutorId, axios]);

  const counterpartyName = who;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteEvent(event.id);
      onClose();
    } catch {
      addToast({ variant: 'error', title: 'Could not cancel lesson.' });
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const copyMeet = async () => {
    try {
      await navigator.clipboard.writeText(event.googleMeetUrl!);
      addToast({ variant: 'success', title: 'Meet link copied' });
    } catch {
      addToast({ variant: 'error', title: 'Failed to copy link' });
    }
  };

  const initials = (name: string) => getInitial(name);


  const handleHomeworkDrop = useCallback((files: File[]) => {
    if (files[0]) {
      setHomeworkFile(files[0]);
      setClearHomeworkFile(false); // A new file overrides a deletion intent
    }
  }, []);

  const handleRemoveHomework = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (homeworkFile) {
      setHomeworkFile(null); // Remove newly staged file
    } else if (event.homeworkFileUrl) {
      setClearHomeworkFile(true); // Mark existing file for deletion
    }
  };

  const handleResponseDrop = useCallback((files: File[]) => {
    if (files[0]) {
      setResponseFile(files[0]);
      setClearResponseFile(false);
    }
  }, []);

  const handleRemoveResponse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (responseFile) {
      setResponseFile(null);
    } else if (event.responseFileUrl) {
      setClearResponseFile(true);
    }
  };

  const typeTag =
    event.eventType === 'lesson' ? (
      <CornerTag color="blue">Lesson</CornerTag>
    ) : (
      <CornerTag color="yellow">Intro Call</CornerTag>
    );

  /* ---------- EDIT MODE state ---------- */
  // CHANGED: More granular permissions as requested
  const canEditTitleAndMeet = !!isTutor; // Title, series, meet link — tutors only
  const canEditTime = true; // Both parents and tutors can edit time
  const canEditTutorFields = !!isTutor;
  const canEditParentFields = !isTutor;
  const canEditAnything = canEditTitleAndMeet || canEditTime || canEditTutorFields || canEditParentFields;

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Per-section inline editing
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [isEditingHomework, setIsEditingHomework] = useState(false);
  const [isEditingResponse, setIsEditingResponse] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [savingHomework, setSavingHomework] = useState(false);
  const [savingResponse, setSavingResponse] = useState(false);

  // Inline time editor state
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [timeEditError, setTimeEditError] = useState('');
  const [editDate, setEditDate] = useState(() => formatInTimeZone(new Date(event.startAt), activeTz, 'yyyy-MM-dd'));
  const [editStartTime, setEditStartTime] = useState(() => formatInTimeZone(new Date(event.startAt), activeTz, 'HH:mm'));
  const [editEndTime, setEditEndTime] = useState(() => formatInTimeZone(new Date(event.endAt), activeTz, 'HH:mm'));

  const cancelTimeEdit = () => {
    setIsEditingTime(false);
    setTimeEditError('');
    setEditDate(formatInTimeZone(new Date(event.startAt), activeTz, 'yyyy-MM-dd'));
    setEditStartTime(formatInTimeZone(new Date(event.startAt), activeTz, 'HH:mm'));
    setEditEndTime(formatInTimeZone(new Date(event.endAt), activeTz, 'HH:mm'));
  };

  // --- Time editor: conflict & availability checking ---
  const editConflictMessage = useMemo(() => {
    if (!editDate || !editStartTime || !editEndTime || !isEditingTime) return null;
    try {
      const newStart = wallClockToISO(editDate, editStartTime, activeTz);
      const newEnd = wallClockEndToISO(editDate, editStartTime, editEndTime, activeTz);
      // Exclude the current event from conflict checks
      const parentConflict = myEvents.find(ev => ev.id !== event.id && ev.startAt < newEnd && ev.endAt > newStart);
      if (parentConflict) return `Conflicts with "${parentConflict.title}".`;
      // Filter out the current event's slot from tutor busy times (it may overlap partially after editing)
      const filteredBusy = tutorBusySlots.filter(s => !(s.startAt === event.startAt && s.endAt === event.endAt));
      const tutorConflict = filteredBusy.find(s => s.startAt < newEnd && s.endAt > newStart);
      if (tutorConflict) return `${counterpartyName || 'Tutor'} has a booking at this time.`;
      return null;
    } catch { return null; }
  }, [editDate, editStartTime, editEndTime, activeTz, myEvents, tutorBusySlots, event.id, event.startAt, event.endAt, counterpartyName, isEditingTime]);

  const editStartInPast = useMemo(() => {
    if (!editDate || !editStartTime || !isEditingTime) return false;
    try { return new Date(wallClockToISO(editDate, editStartTime, activeTz)).getTime() <= Date.now(); } catch { return false; }
  }, [editDate, editStartTime, activeTz, isEditingTime]);

  // Tutor availability slots for the selected day (shifted to activeTz)
  const editDayIndex = useMemo(() => {
    if (!editDate) return -1;
    const [y, m, d] = editDate.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }, [editDate]);

  const DAY_NAMES: (keyof AvailabilityObject)[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const editTutorSlots = useMemo(() => {
    if (editDayIndex < 0 || !tutorAvailability || !tutorTz) return [];
    let avail = tutorAvailability;
    if (activeTz !== tutorTz) {
      const offset = getTimezoneOffset(activeTz, tutorTz);
      avail = adjustAvailabilityForTimezone(tutorAvailability, offset, DAYS_SUNDAY_FIRST);
    }
    return (avail[DAY_NAMES[editDayIndex]] || []).map(s => ({ start: s.start, end: s.end }));
  }, [tutorAvailability, editDayIndex, tutorTz, activeTz]);

  const minutesToStr = (mins: number) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

  const editAvailChips = useMemo(() => {
    if (editTutorSlots.length === 0) return [];
    const chips: { start: number; end: number; label: string }[] = [];
    for (const slot of editTutorSlots) {
      for (let t = slot.start; t + 60 <= slot.end; t += GRANULARITY) {
        chips.push({ start: t, end: t + 60, label: `${minutesToStr(t)} – ${minutesToStr(t + 60)}` });
      }
    }
    return chips;
  }, [editTutorSlots]);

  const editOutsideAvailability = useMemo(() => {
    if (!editStartTime || !editEndTime || editTutorSlots.length === 0) return false;
    const [sh, sm] = editStartTime.split(':').map(Number);
    const [eh, em] = editEndTime.split(':').map(Number);
    return !editTutorSlots.some(s => sh * 60 + sm >= s.start && eh * 60 + em <= s.end);
  }, [editStartTime, editEndTime, editTutorSlots]);

  // Cross-tz preview in editor (show counterparty's time)
  const editCrossTzPreview = useMemo(() => {
    if (!isEditingTime || !editDate || !editStartTime || !editEndTime) return null;
    const cpTz = isTutor ? counterpartyTz : tutorTz;
    if (!cpTz || cpTz === activeTz) return null;
    try {
      const startUtc = new Date(wallClockToISO(editDate, editStartTime, activeTz));
      const endUtc = new Date(wallClockEndToISO(editDate, editStartTime, editEndTime, activeTz));
      return formatRangeInTz(startUtc, endUtc, cpTz, { includeDate: true });
    } catch { return null; }
  }, [isEditingTime, editDate, editStartTime, editEndTime, activeTz, counterpartyTz, tutorTz, isTutor]);

  const editCrossTzLabel = isTutor ? 'Parent' : (counterpartyName || 'Tutor');

  const saveTimeEdit = async () => {
    if (!editDate || !editStartTime || !editEndTime) return;
    if (editStartTime === editEndTime) {
      setTimeEditError('End time cannot be the same as start time.');
      return;
    }
    if (editConflictMessage) {
      setTimeEditError(editConflictMessage);
      return;
    }
    if (editStartInPast) {
      setTimeEditError('Lesson cannot start in the past.');
      return;
    }
    setSavingTime(true);
    setTimeEditError('');
    try {
      const newStartISO = wallClockToISO(editDate, editStartTime, activeTz);
      const newEndISO = wallClockEndToISO(editDate, editStartTime, editEndTime, activeTz);

      const updates: Partial<CalendarEvent> = {};
      if (newStartISO !== event.startAt) updates.startAt = newStartISO;
      if (newEndISO !== event.endAt) updates.endAt = newEndISO;

      if (Object.keys(updates).length === 0) {
        setIsEditingTime(false);
        setSavingTime(false);
        return;
      }

      await updateEvent({ eventId: event.id, updates });
      setIsEditingTime(false);
    } catch {
      addToast({ variant: 'error', title: 'Could not update time.' });
    } finally {
      setSavingTime(false);
    }
  };

  // Per-section save handlers
  const saveFeedback = async () => {
    setSavingFeedback(true);
    try {
      const updates: Partial<CalendarEvent> = {};
      if ((feedbackText || null) !== (event.feedbackText || null)) updates.feedbackText = feedbackText || null;
      if (Object.keys(updates).length === 0) { setIsEditingFeedback(false); return; }
      await updateEvent({ eventId: event.id, updates });
      setIsEditingFeedback(false);
    } catch { addToast({ variant: 'error', title: 'Could not save feedback.' }); }
    finally { setSavingFeedback(false); }
  };

  const saveHomework = async () => {
    setSavingHomework(true);
    try {
      const updates: Partial<CalendarEvent> = {};
      if ((homeworkText || null) !== (event.homeworkText || null)) updates.homeworkText = homeworkText || null;
      if (clearHomeworkFile) { updates.homeworkFileUrl = null; }
      else if (homeworkFile) { updates.homeworkFileUrl = await uploadFile(homeworkFile); }
      if (Object.keys(updates).length === 0) { setIsEditingHomework(false); return; }
      await updateEvent({ eventId: event.id, updates });
      setIsEditingHomework(false);
    } catch { addToast({ variant: 'error', title: 'Could not save homework.' }); }
    finally { setSavingHomework(false); }
  };

  const saveResponse = async () => {
    setSavingResponse(true);
    try {
      const updates: Partial<CalendarEvent> = {};
      if ((responseText || null) !== (event.responseText || null)) updates.responseText = responseText || null;
      if (clearResponseFile) { updates.responseFileUrl = null; }
      else if (responseFile) { updates.responseFileUrl = await uploadFile(responseFile); }
      if (Object.keys(updates).length === 0) { setIsEditingResponse(false); return; }
      await updateEvent({ eventId: event.id, updates });
      setIsEditingResponse(false);
    } catch { addToast({ variant: 'error', title: 'Could not save response.' }); }
    finally { setSavingResponse(false); }
  };

  // Form state
  const [title, setTitle] = useState(event.title || '');
  const [seriesTitle, setSeriesTitle] = useState(event.seriesTitle || '');
  const [startLocal, setStartLocal] = useState(toTzInputValue(event.startAt, activeTz));
  const [endLocal, setEndLocal] = useState(toTzInputValue(event.endAt, activeTz));
  const [meetUrl, setMeetUrl] = useState(event.googleMeetUrl || '');
  const [feedbackText, setFeedbackText] = useState(event.feedbackText || '');
  const [homeworkText, setHomeworkText] = useState(event.homeworkText || '');
  const [homeworkFile, setHomeworkFile] = useState<File | null>(null);
  const [clearHomeworkFile, setClearHomeworkFile] = useState(false);
  const [responseText, setResponseText] = useState(event.responseText || '');
  const [responseFile, setResponseFile] = useState<File | null>(null);
  const [clearResponseFile, setClearResponseFile] = useState(false);

  const cancelEdit = () => {
    setIsEditing(false);
    // Reset all form fields to original event data
    setTitle(event.title || '');
    setSeriesTitle(event.seriesTitle || '');
    setStartLocal(toTzInputValue(event.startAt, activeTz));
    setEndLocal(toTzInputValue(event.endAt, activeTz));
    setMeetUrl(event.googleMeetUrl || '');
    setFeedbackText(event.feedbackText || '');
    setHomeworkText(event.homeworkText || '');
    setHomeworkFile(null);
    setResponseText(event.responseText || '');
    setResponseFile(null);
    setClearHomeworkFile(false);
    setClearResponseFile(false);
  };

  const uploadFile = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(endpoints.upload, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = res?.data?.url || res?.data?.fileUrl || res?.data?.location;
    if (!url) throw new Error('Upload did not return a URL');
    return url as string;
  };
  
  const onSave = async () => {
    try {
      setSaving(true);
  
      const newStartISO = fromTzInputValue(startLocal, activeTz);
      const newEndISO = fromTzInputValue(endLocal, activeTz);
      // CHANGED: Validation now only checks time, as both can edit it
      if (canEditTime && new Date(newEndISO) <= new Date(newStartISO)) {
        addToast({ variant: 'error', title: 'End time must be after start time.' });
        setSaving(false);
        return;
      }
  
      const updates: Partial<CalendarEvent> = {};
  
      // CHANGED: Granular checks based on new permissions
      if (canEditTitleAndMeet) {
        if (title !== event.title) updates.title = title;
        if ((seriesTitle || null) !== (event.seriesTitle || null)) updates.seriesTitle = seriesTitle || null;
        if ((meetUrl || null) !== (event.googleMeetUrl || null)) updates.googleMeetUrl = meetUrl || null;
      }
      
      if (canEditTime) {
          if (newStartISO !== event.startAt) updates.startAt = newStartISO;
          if (newEndISO !== event.endAt) updates.endAt = newEndISO;
      }
  
      if (canEditTutorFields) {
        if ((feedbackText || null) !== (event.feedbackText || null)) updates.feedbackText = feedbackText || null;
        if ((homeworkText || null) !== (event.homeworkText || null)) updates.homeworkText = homeworkText || null;
  
        if (clearHomeworkFile) {
          updates.homeworkFileUrl = null;
        } else if (homeworkFile) {
          const url = await uploadFile(homeworkFile);
          updates.homeworkFileUrl = url;
        }
      }
  
      if (canEditParentFields) {
        if ((responseText || null) !== (event.responseText || null)) updates.responseText = responseText || null;
  
        if (clearResponseFile) {
          updates.responseFileUrl = null;
        } else if (responseFile) {
          const url = await uploadFile(responseFile);
          updates.responseFileUrl = url;
        }
      }
  
      if (Object.keys(updates).length === 0) {
        addToast({ variant: 'info', title: 'Nothing to update.' });
        setIsEditing(false);
        setSaving(false);
        return;
      }
  
      await updateEvent({ eventId: event.id, updates });
      setIsEditing(false);
    } catch {
      addToast({ variant: 'error', title: 'Could not save changes.' });
    } finally {
      setSaving(false);
    }
  };

  const originalHomeworkName = useMemo(() => getFileName(event.homeworkFileUrl || ''), [event.homeworkFileUrl]);
  const homeworkNameToDisplay = homeworkFile?.name || (!clearHomeworkFile && originalHomeworkName) || '';
  
  const originalResponseName = useMemo(() => getFileName(event.responseFileUrl || ''), [event.responseFileUrl]);
  const responseNameToDisplay = responseFile?.name || (!clearResponseFile && originalResponseName) || '';

  return (
    <DetailPageShell>
      <CardHeader>

          <TruncatedTitle>{fullTitle}</TruncatedTitle>



          <BannerIconButton aria-label="Close" onClick={onClose}>
            <XCircleFill size={18} />
            {/* <X size={22} /> */}
          </BannerIconButton>
        {/* </div> */}
      </CardHeader>

      <CardContent>
            <TutorDetailWrapper2>
              {avatar?.photoUrl ? (
                <TutorImage src={avatar.photoUrl} alt={avatar.name || who} />
              ) : (
                <ProfileInitial view={'detail'} style={{ backgroundColor: avatar?.color || '#e0e0e0' }}>
                  {initials(avatar?.name || who)}
                </ProfileInitial>
              )}
              </TutorDetailWrapper2>

              <HeaderInfo css={{paddingTop: '1rem'}}>
                {/* <NameAndPriceWrapper> */}
                  <Title>{fullTitle}</Title>
                {/* </NameAndPriceWrapper> */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '0.5rem', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', flex: 1 }}>
                    <When>{date}</When>
                    <Subtle>{range}</Subtle>
                  </div>
                  {canEditTime && !isEditingTime && (
                    <button
                      type="button"
                      onClick={() => setIsEditingTime(true)}
                      aria-label="Edit time"
                      style={{
                        all: 'unset',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.2rem',
                        borderRadius: '0.3rem',
                        color: 'var(--colors-sand10)',
                        transition: 'color 0.15s',
                        marginLeft: 'auto',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--colors-blue11)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--colors-sand10)')}
                    >
                      <PencilSquare size={14} />
                    </button>
                  )}
                </div>

                {!isEditingTime && (
                  <p style={{ color: '#6b7280', fontSize: '0.7rem', margin: '0.25rem 0 0', textAlign: 'center' }}>
                    Times shown in {friendlyTzName(activeTz)}
                  </p>
                )}

                {/* Inline time editor */}
                {isEditingTime && (
                  <div style={{
                    marginTop: '0.75rem',
                    marginBottom: '0.25rem',
                    padding: '0.75rem 1rem',
                    borderRadius: 8,
                    border: '1px solid var(--colors-sand6, #e5e7eb)',
                    background: 'var(--colors-sand2, #fafafa)',
                    display: 'grid',
                    gap: '0.6rem',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}>
                    <p style={{ color: '#6b7280', fontSize: '0.7rem', margin: 0 }}>
                      Times in {friendlyTzName(activeTz)}
                    </p>

                    {/* Availability chips */}
                    {editAvailChips.length > 0 && (
                      <div>
                        <p style={{ color: '#6b7280', fontSize: '0.7rem', marginBottom: '0.35rem' }}>
                          {counterpartyName ? `${counterpartyName}'s` : "Tutor's"} available slots:
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                          {editAvailChips.map((chip) => {
                            const sel = editStartTime === minutesToStr(chip.start) && editEndTime === minutesToStr(chip.end);
                            return (
                              <button
                                key={chip.start}
                                type="button"
                                onClick={() => { setEditStartTime(minutesToStr(chip.start)); setEditEndTime(minutesToStr(chip.end)); }}
                                style={{
                                  padding: '0.25rem 0.5rem', fontSize: '0.7rem', borderRadius: 5,
                                  border: sel ? '1.5px solid var(--colors-blue9)' : '1px solid #d1d5db',
                                  background: sel ? 'var(--colors-blue3)' : '#fff',
                                  color: sel ? 'var(--colors-blue11)' : '#374151',
                                  cursor: 'pointer', fontWeight: sel ? 600 : 400, transition: 'all 0.15s',
                                }}
                              >
                                {chip.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '0.5rem' }}>
                      <div>
                        <label style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--colors-sand11)', display: 'block', marginBottom: '0.2rem' }}>Date</label>
                        <input
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                          style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid var(--colors-sand7)', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--colors-sand11)', display: 'block', marginBottom: '0.2rem' }}>Start</label>
                        <input
                          type="time"
                          value={editStartTime}
                          onChange={(e) => setEditStartTime(e.target.value)}
                          style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid var(--colors-sand7)', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--colors-sand11)', display: 'block', marginBottom: '0.2rem' }}>End</label>
                        <input
                          type="time"
                          value={editEndTime}
                          onChange={(e) => setEditEndTime(e.target.value)}
                          style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid var(--colors-sand7)', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    {/* Warnings & errors */}
                    {editStartInPast && (
                      <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: 0 }}>Lesson cannot start in the past.</p>
                    )}
                    {editOutsideAvailability && !editConflictMessage && !editStartInPast && (
                      <p style={{ color: '#d97706', fontSize: '0.75rem', margin: 0 }}>
                        Outside {counterpartyName ? `${counterpartyName}'s` : "tutor's"} stated availability.
                      </p>
                    )}
                    {editConflictMessage && !editStartInPast && (
                      <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: 0 }}>{editConflictMessage}</p>
                    )}
                    {timeEditError && (
                      <p style={{ color: '#dc2626', fontSize: '0.75rem', margin: 0 }}>{timeEditError}</p>
                    )}

                    {/* Cross-tz preview */}
                    {editCrossTzPreview && (
                      <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                        {editCrossTzLabel}'s time: {editCrossTzPreview}
                      </p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <SmallButton type="button" onClick={cancelTimeEdit}>Cancel</SmallButton>
                      <SmallButton
                        type="button"
                        onClick={saveTimeEdit}
                        disabled={savingTime || !!editConflictMessage || editStartInPast}
                        style={{ background: 'var(--colors-blue9)', color: 'white', border: 'none' }}
                      >
                        {savingTime ? 'Saving...' : 'Save'}
                      </SmallButton>
                    </div>
                  </div>
                )}

                {tzSwitchButtons.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', flexWrap: 'wrap', width: '100%', marginTop: '0.5rem' }}>
                    {tzSwitchButtons.map(btn => (
                      <button
                        key={btn.key}
                        type="button"
                        onClick={() => setActiveTzKey(btn.key)}
                        style={{
                          all: 'unset',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.4rem',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          border: '1px solid var(--colors-sand7, #d4d4d4)',
                          color: 'var(--colors-sand12, #1a1a1a)',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--colors-sand4, #f0f0f0)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <Globe size={12} />
                        View in {btn.label}
                      </button>
                    ))}
                  </div>
                )}
                <TagWrap css={{ justifyContent: 'center', width: '100%', marginTop: '0.5rem' }}>
                  {event.seriesTitle && <CornerTag color="gray">Series: {event.seriesTitle}</CornerTag>}
                  {typeTag}
                </TagWrap>
              </HeaderInfo>


        {/* FEEDBACK (tutor edits) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SubheaderContainer css={{ flex: 1 }}>
            <SubheaderLine />
            <SubheaderTitle>Feedback</SubheaderTitle>
            <SubheaderLine />
          </SubheaderContainer>
          {!isEditingFeedback && canEditTutorFields && (
            <button
              type="button"
              onClick={() => setIsEditingFeedback(true)}
              aria-label="Edit feedback"
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'center', padding: '0.2rem', borderRadius: '0.3rem',
                color: 'var(--colors-sand10)',
                transition: 'color 0.15s', marginLeft: '0.5rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--colors-blue11)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--colors-sand10)'; }}
            >
              <PencilSquare size={14} />
            </button>
          )}
        </div>
        <Section>
          {!isEditingFeedback ? (
            <DescriptionText className={!event.feedbackText ? 'not-provided' : ''}>
              {event.feedbackText || 'No feedback yet.'}
            </DescriptionText>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <OverlayTextarea
                as="textarea"
                rows={4}
                maxLength={1000}
                value={feedbackText || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFeedbackText(e.target.value)}
                placeholder="How did the lesson go? What to improve next time?"
              />
              <small style={{ alignSelf: 'flex-end', color: 'var(--colors-sand10)', fontSize: '0.75rem' }}>
                {(feedbackText ?? '').length}/1000
              </small>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <SmallButton type="button" onClick={() => { setFeedbackText(event.feedbackText || ''); setIsEditingFeedback(false); }}>Cancel</SmallButton>
                <SmallButton type="button" onClick={saveFeedback} disabled={savingFeedback} style={{ background: 'var(--colors-blue9)', color: 'white', border: 'none' }}>
                  {savingFeedback ? 'Saving...' : 'Save'}
                </SmallButton>
              </div>
            </div>
          )}
        </Section>

        {/* HOMEWORK (tutor edits) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SubheaderContainer css={{ flex: 1 }}>
            <SubheaderLine />
            <SubheaderTitle>Homework</SubheaderTitle>
            <SubheaderLine />
          </SubheaderContainer>
          {!isEditingHomework && canEditTutorFields && (
            <button
              type="button"
              onClick={() => setIsEditingHomework(true)}
              aria-label="Edit homework"
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'center', padding: '0.2rem', borderRadius: '0.3rem',
                color: 'var(--colors-sand10)',
                transition: 'color 0.15s', marginLeft: '0.5rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--colors-blue11)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--colors-sand10)'; }}
            >
              <PencilSquare size={14} />
            </button>
          )}
        </div>
        <Section>
          {!isEditingHomework ? (
            <>
              <DescriptionText className={!event.homeworkText ? 'not-provided' : ''}>
                {event.homeworkText || 'No written homework provided.'}
              </DescriptionText>
              {event.homeworkFileUrl && (
                <FileRow>
                  <Paperclip />
                  <LinkButton href={event.homeworkFileUrl} target="_blank" rel="noreferrer">Open homework file</LinkButton>
                </FileRow>
              )}
            </>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <OverlayTextarea
                as="textarea"
                rows={4}
                maxLength={1000}
                value={homeworkText || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setHomeworkText(e.target.value)}
                placeholder="Tasks, due date, instructions..."
              />
              <small style={{ alignSelf: 'flex-end', color: 'var(--colors-sand10)', fontSize: '0.75rem' }}>
                {(homeworkText ?? '').length}/1000
              </small>
              <Dropzone onDrop={handleHomeworkDrop} accept={{ 'application/pdf': [], 'image/*': []}} maxFiles={1}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <DropzoneContainer {...getRootProps()} state={homeworkNameToDisplay ? 'success' : 'idle'} css={{ cursor: 'pointer', background: isDragActive ? '$elementBackgroundHover' : '$elementBackground' }}>
                    <input {...getInputProps()} />
                    {homeworkNameToDisplay ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 0.5rem' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{homeworkNameToDisplay}</span>
                        <X size={22} onClick={handleRemoveHomework} style={{ cursor: 'pointer', flexShrink: 0 }} />
                      </div>
                    ) : (<p>Drag file here or click to select</p>)}
                  </DropzoneContainer>
                )}
              </Dropzone>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <SmallButton type="button" onClick={() => { setHomeworkText(event.homeworkText || ''); setHomeworkFile(null); setClearHomeworkFile(false); setIsEditingHomework(false); }}>Cancel</SmallButton>
                <SmallButton type="button" onClick={saveHomework} disabled={savingHomework} style={{ background: 'var(--colors-blue9)', color: 'white', border: 'none' }}>
                  {savingHomework ? 'Saving...' : 'Save'}
                </SmallButton>
              </div>
            </div>
          )}
        </Section>

        {/* RESPONSE / SUBMISSION (parent edits) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SubheaderContainer css={{ flex: 1 }}>
            <SubheaderLine />
            <SubheaderTitle>Response / Submission</SubheaderTitle>
            <SubheaderLine />
          </SubheaderContainer>
          {!isEditingResponse && canEditParentFields && (
            <button
              type="button"
              onClick={() => setIsEditingResponse(true)}
              aria-label="Edit response"
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'center', padding: '0.2rem', borderRadius: '0.3rem',
                color: 'var(--colors-sand10)',
                transition: 'color 0.15s', marginLeft: '0.5rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--colors-blue11)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--colors-sand10)'; }}
            >
              <PencilSquare size={14} />
            </button>
          )}
        </div>
        <Section>
          {!isEditingResponse ? (
            <>
              <DescriptionText className={!event.responseText ? 'not-provided' : ''}>
                {event.responseText || 'No response yet.'}
              </DescriptionText>
              {event.responseFileUrl && (
                <FileRow>
                  <Paperclip />
                  <LinkButton href={event.responseFileUrl} target="_blank" rel="noreferrer">Open submitted file</LinkButton>
                </FileRow>
              )}
            </>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <OverlayTextarea
                as="textarea"
                rows={4}
                maxLength={1000}
                value={responseText || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setResponseText(e.target.value)}
                placeholder="Notes back to the tutor, or a brief about the uploaded work."
              />
              <small style={{ alignSelf: 'flex-end', color: 'var(--colors-sand10)', fontSize: '0.75rem' }}>{(responseText ?? '').length}/1000</small>
              <Dropzone onDrop={handleResponseDrop} accept={{ 'application/pdf': [], 'image/*': []}} maxFiles={1}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <DropzoneContainer {...getRootProps()} state={responseNameToDisplay ? 'success' : 'idle'} css={{ cursor: 'pointer', background: isDragActive ? '$elementBackgroundHover' : '$elementBackground' }}>
                    <input {...getInputProps()} />
                    {responseNameToDisplay ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 0.5rem' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{responseNameToDisplay}</span>
                        <X size={22} onClick={handleRemoveResponse} style={{ cursor: 'pointer', flexShrink: 0 }} />
                      </div>
                    ) : (<p>Drag file here or click to select</p>)}
                  </DropzoneContainer>
                )}
              </Dropzone>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <SmallButton type="button" onClick={() => { setResponseText(event.responseText || ''); setResponseFile(null); setClearResponseFile(false); setIsEditingResponse(false); }}>Cancel</SmallButton>
                <SmallButton type="button" onClick={saveResponse} disabled={savingResponse} style={{ background: 'var(--colors-blue9)', color: 'white', border: 'none' }}>
                  {savingResponse ? 'Saving...' : 'Save'}
                </SmallButton>
              </div>
            </div>
          )}
        </Section>
      </CardContent>

      <CardFooter2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {canEditParentFields && !confirmingDelete && new Date(event.startAt).getTime() > Date.now() && (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              style={{
                all: 'unset',
                padding: '0.4rem 0.65rem',
                fontSize: '0.85rem',
                borderRadius: 8,
                border: '1px solid #fca5a5',
                color: '#dc2626',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Cancel Lesson
            </button>
          )}
          {confirmingDelete && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#dc2626' }}>Are you sure?</span>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  all: 'unset',
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.8rem',
                  borderRadius: 6,
                  background: '#dc2626',
                  color: 'white',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? 'Cancelling...' : 'Yes, cancel'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                style={{
                  all: 'unset',
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.8rem',
                  borderRadius: 6,
                  border: '1px solid var(--colors-sand11)',
                  cursor: 'pointer',
                }}
              >
                No
              </button>
            </div>
          )}
        </div>
        <MeetContainer>
          <MeetLinkBox
            role="button"
            onClick={() => navigate(`/room/${event.id}`)}
            css={{ cursor: 'pointer' }}
          >
            <CameraVideo size={16} /> Join lesson
          </MeetLinkBox>
        </MeetContainer>
      </CardFooter2>
    </DetailPageShell>
  );
};
